/**
 * client/src/utils/destination-connexion — Où aller après la connexion.
 * @author Dr Hamid MADANI <drmdh@msn.com>
 *
 * ─── LE DÉFAUT CORRIGÉ (2026-09-17) ──────────────────────────────────────────
 *
 * Le guichet Android ouvre `/counter`. Non connecté, la garde de route renvoyait vers `/login`
 * SANS retenir la page demandée ; après connexion, la page de login choisissait selon le seul
 * rôle — et un admin partait sur `/manage`. Le poste de guichet ouvrait donc l'administration.
 * Même chose après une déconnexion depuis le guichet, et après le changement forcé du mot de
 * passe par défaut.
 *
 * ─── LA RÈGLE ────────────────────────────────────────────────────────────────
 *
 * On revient là où l'on voulait aller (`from`), si le rôle y a droit. Sinon, l'écran par défaut
 * du rôle : `/manage` pour la gestion, `/counter` pour un agent.
 *
 * `/counter` et non `/counter-select` pour un agent sans guichet attribué : cette route n'existe
 * pas dans `App.jsx` — l'agent atterrissait sur la page 404. `CounterApp` gère lui-même le choix
 * du guichet.
 *
 * `from` ne vient que de l'état de navigation interne (jamais d'un paramètre d'URL) et doit être
 * un chemin local : pas de redirection ouverte vers un autre site.
 *
 * Module PUR (aucun import React) : testé directement par `apps/server/test-scripts`.
 */

/**
 * Écrans réservés à la gestion (admin/manager). MIROIR des routes protégées par
 * `allowedRoles={['admin', 'manager']}` dans `App.jsx` — un test vérifie que les deux listes
 * restent identiques.
 */
export const ECRANS_GESTION = ['/manage', '/displays', '/sound', '/numbering', '/ticker', '/audit', '/config', '/backup'];

/** Écrans vers lesquels revenir n'a pas de sens : on y repasserait en boucle. */
const ECRANS_DE_PASSAGE = ['/login', '/change-password', '/unauthorized'];

const estGestion = (role) => role === 'admin' || role === 'manager';

/** Écran d'arrivée d'un rôle quand rien d'autre n'a été demandé. */
export function ecranParDefaut(role) {
  return estGestion(role) ? '/manage' : '/counter';
}

/** Destination après connexion (ou après le changement forcé du mot de passe). */
export function destinationApresConnexion({ role, from } = {}) {
  const defaut = ecranParDefaut(role);
  if (typeof from !== 'string' || !from.startsWith('/') || from.startsWith('//')) return defaut;
  const chemin = from.split(/[?#]/)[0];
  if (ECRANS_DE_PASSAGE.includes(chemin)) return defaut;
  const reserveGestion = ECRANS_GESTION.some((e) => chemin === e || chemin.startsWith(`${e}/`));
  if (reserveGestion && !estGestion(role)) return defaut;
  return from;
}

/** État de navigation à transmettre à `/login` pour revenir ensuite sur la page courante. */
export function retourVers(location) {
  return { from: `${location?.pathname || ''}${location?.search || ''}` };
}
